import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Shield, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useOnHold } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_COLORS: Record<string, string> = {
  DOG: "hsl(var(--chart-1))",
  CAT: "hsl(var(--chart-2))",
  OTHER: "hsl(var(--chart-3))",
};

const SPECIES_KEY_MAP: Record<string, string> = {
  dogs: "DOG",
  cats: "CAT",
  other: "OTHER",
};

interface OnHoldChartProps {}

function StatCard({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  );
}

export function OnHoldChart({}: OnHoldChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const { data, isLoading, isError, refetch } = useOnHold(location);
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const chartData = useMemo(() => {
    if (!data) return [];
    const allSpecies = [
      { species: "Dogs", key: "DOG", count: data.bySpecies.DOG || 0, fill: SPECIES_COLORS.DOG },
      { species: "Cats", key: "CAT", count: data.bySpecies.CAT || 0, fill: SPECIES_COLORS.CAT },
      { species: "Other", key: "OTHER", count: data.bySpecies.OTHER || 0, fill: SPECIES_COLORS.OTHER },
    ];

    if (speciesFilter === "all") return allSpecies;
    const key = SPECIES_KEY_MAP[speciesFilter];
    return allSpecies.filter((s) => s.key === key);
  }, [data, speciesFilter]);

  const totalFiltered = useMemo(() => {
    if (!data) return 0;
    if (speciesFilter === "all") return data.total;
    const key = SPECIES_KEY_MAP[speciesFilter];
    return data.bySpecies[key] || 0;
  }, [data, speciesFilter]);

  return (
    <Card data-testid="card-on-hold" className="min-h-[400px]">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-muted-foreground" />
            Animals on Hold
          </CardTitle>
        </div>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-on-hold">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dogs">Dogs</SelectItem>
            <SelectItem value="cats">Cats</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4" data-testid="loading-on-hold">
            <div className="flex gap-3">
              <Skeleton className="h-16 flex-1" />
              <Skeleton className="h-16 flex-1" />
            </div>
            <Skeleton className="h-[200px] w-full" />
          </div>
        )}
        {isError && (
          <Alert variant="destructive" data-testid="error-on-hold">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load on-hold data</span>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-on-hold">
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {data && totalFiltered === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-on-hold">
            No animals currently on hold{speciesFilter !== "all" ? ` for ${speciesFilter}` : ""}.
          </div>
        )}
        {data && totalFiltered > 0 && (
          <>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-1" data-testid="text-on-hold-stats-hint">
                Currently on hold
              </p>
              <div className="flex flex-wrap gap-3">
                <StatCard label="Total On Hold" value={totalFiltered} testId="stat-total-on-hold" />
                {speciesFilter === "all" && (
                  <>
                    <StatCard label="Dogs" value={data.bySpecies.DOG || 0} testId="stat-on-hold-dogs" />
                    <StatCard label="Cats" value={data.bySpecies.CAT || 0} testId="stat-on-hold-cats" />
                    <StatCard label="Other" value={data.bySpecies.OTHER || 0} testId="stat-on-hold-other" />
                  </>
                )}
              </div>
            </div>
            <div className="h-[200px]" data-testid="chart-on-hold">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="species" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value: number) => [`${value} animals`, "On Hold"]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      borderColor: "hsl(var(--border))",
                      borderRadius: "6px",
                      color: "hsl(var(--card-foreground))",
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
